import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { Project } from 'src/app/model/project/project';
import {
  SUB_PROJECT_TYPES,
  SubProject,
} from 'src/app/model/project/sub-project';
import { IPBoardDesignDialogComponent } from 'src/app/pages/projects/client-corner/create-view-base-project/client-base-project-summary/dialog/ip-board-design-dialog/ip-board-design-dialog.component';
import { IPBoardOnHoldDialogComponent } from 'src/app/pages/projects/client-corner/create-view-base-project/client-base-project-summary/dialog/ip-board-on-hold-dialog/ip-board-on-hold-dialog.component';
import { IPBoardPrintworkDialogComponent } from 'src/app/pages/projects/client-corner/create-view-base-project/client-base-project-summary/dialog/ip-board-printwork-dialog/ip-board-printwork-dialog.component';
import { ClientBaseProjectSummaryHelper } from 'src/app/pages/projects/client-corner/create-view-base-project/client-base-project-summary/helper/client-base-project-summary-helper';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import {
  ProjectDisplayObservableService,
  SignSelectedEvent,
} from 'src/app/services/project-display-observable.service';
import { SnackService } from 'src/app/services/snack.service';
import { TabsService } from 'src/app/services/tabs.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import {
  ToggleSubProjectApprovalControllerRequestService,
  ToggleSubProjectApprovalResponse,
} from 'src/app/services/api/toggle-sub-project-approval/toggle-sub-project-approval-controller-request.service';
import { StockForSignControllerRequestService } from 'src/app/pages/projects/client-corner/create-view-base-project/client-base-project-summary/client-product-summary/client-ip-board-summary/stock-for-sign-controller-request.service';
import { BaseProject } from 'src/app/model/project/base-project';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { ActivatedRoute } from '@angular/router';
import { CreateViewBaseProjectHelper } from 'src/app/pages/projects/client-corner/create-view-base-project/create-view-base-project-helper';
import ProjectHelper from '../../../../../helpers/project-helper';
import { STATUS } from '../../../../../model/project/status';

@Component({
  selector: 'client-detail-actions',
  templateUrl: './client-detail-actions.component.html',
  styleUrls: ['./client-detail-actions.component.scss'],
})
export class ClientDetailActionsComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit
{
  @Input()
  subProject: SubProject;

  @Input()
  project?: Project;

  @Input()
  submitProjectResult: Subject<any>;

  @Input()
  client: boolean = true;

  @Output()
  save = new EventEmitter<any>();

  @Output()
  submitProject = new EventEmitter<any>();

  submitting: boolean = false;

  addressWasHidden: boolean = false;
  designState: boolean = false;
  approvalBlockedByDesign: boolean = false;
  approvalBlockedByStockShortage: boolean = false;
  loadBeforeClose: boolean = false;
  openedFromAppointment: boolean = false;

  constructor(
    private tabsService: TabsService,
    private snackService: SnackService,
    private modalService: ModalService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private stockForSignControllerRequestService: StockForSignControllerRequestService,
    private toggleSubProjectApprovalControllerRequestService: ToggleSubProjectApprovalControllerRequestService,
    private route: ActivatedRoute
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.client) {
      this.hideActionButtonsIfOpenedFromAppointment();
    }
    this.initializeDesignState();
    this.approvalBlockedByDesign = this.calculateApprovalBlockedByDesign();
    this.calculateApprovalBlockedByStockShortage();
    this.addSubscription(
      this.projectDisplayObservableService.getAddressWasHiddenSubscribable(),
      (b) => {
        this.addressWasHidden = b;
      }
    );

    if (this.submitProjectResult != null) {
      this.addSubscription(
        this.submitProjectResult,
        () => (this.submitting = false)
      );
    }

    if (!this.client) {
      const approvalBlockedByDesignObservable =
        this.createObservableUntilDestroy(
          this.projectDisplayObservableService.getProductAssetsChangedSubscribable(),
          this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable()
        );
      this.addSubscription(
        approvalBlockedByDesignObservable,
        () =>
          (this.approvalBlockedByDesign =
            this.calculateApprovalBlockedByDesign())
      );
      this.addSubscription(
        this.projectDisplayObservableService.getProductDesignVariableChangedSubscribable(),
        () => this.initializeDesignState()
      );
      this.addSubscription(
        this.projectDisplayObservableService.getSignSelectedSubscribable(),
        (event: SignSelectedEvent) => {
          if (
            IPBoardHelper.signSelectedEventRelevantForProject(
              event,
              this.project
            )
          ) {
            this.approvalBlockedByStockShortage = false;
          }
        }
      );
    }
  }

  private hideActionButtonsIfOpenedFromAppointment(): void {
    this.addSubscription(
      this.route.fragment,
      (fragment: string) =>
        (this.openedFromAppointment = fragment === 'appointment')
    );
  }

  private initializeDesignState(): void {
    if (this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      this.designState =
        CollectionHelper.isPopulated(this.project.products) &&
        this.project.products[0].needsDesign &&
        !this.project.products[0].designFinished;
    } else {
      this.designState = false; // TODO implement for adjustments if this component is ever used for adjustments
    }
  }

  private calculateApprovalBlockedByDesign(): boolean {
    if (
      this.client ||
      this.subProject.type !== SUB_PROJECT_TYPES.BASE_PROJECT ||
      this.project == null ||
      !CollectionHelper.isPopulated(this.project.products)
    ) {
      return false;
    }
    return this.project.products.some((p) => {
      if (!ProductHelper.isIPBoard(p)) {
        return false;
      }

      const ipBoard = <IPBoard>p;
      return (
        (ipBoard.draftDesign ||
          (ipBoard.boardPart != null && ipBoard.boardPart.sign == null)) &&
        (!CollectionHelper.isPopulated(p.productAssets) ||
          !p.productAssets.some(
            (pa) => !pa.archived && pa.formSection === 'extraDesign'
          ))
      );
    });
  }

  private calculateApprovalBlockedByStockShortage(): void {
    this.approvalBlockedByStockShortage =
      this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
      (<BaseProject>this.subProject).stockShortageOnCreate &&
      this.ipBoardMightHaveStockShortage();
    if (this.approvalBlockedByStockShortage) {
      this.doCheckStockShortage().then(
        (stockShortage: boolean) =>
          (this.approvalBlockedByStockShortage = stockShortage)
      );
    }
  }

  private ipBoardMightHaveStockShortage(): boolean {
    if (!CollectionHelper.isPopulated(this.project.products)) {
      return false;
    }
    for (const product of this.project.products) {
      if (
        ProductHelper.isIPBoard(product) &&
        ((product.boardPart != null &&
          product.boardPart.sign != null &&
          !product.boardPart.sign.withoutStock) ||
          (product.otherSideBoardPart != null &&
            product.otherSideBoardPart.sign != null &&
            !product.otherSideBoardPart.sign.withoutStock))
      ) {
        return true;
      }
    }
    return false;
  }

  private async doCheckStockShortage(): Promise<boolean> {
    const checkStockShortagePromises: Promise<boolean>[] = [];
    for (const product of this.project.products) {
      if (ProductHelper.isIPBoard(product)) {
        checkStockShortagePromises.push(
          this.getCheckStockShortagePromise(
            product.boardPart,
            product.otherSideBoardPart
          )
        );
        checkStockShortagePromises.push(
          this.getCheckStockShortagePromise(
            product.otherSideBoardPart,
            product.boardPart
          )
        );
      }
    }
    if (checkStockShortagePromises.length === 0) {
      return false;
    }
    const resolvedPromises = await Promise.all(checkStockShortagePromises);
    return resolvedPromises.some((p) => p);
  }

  private getCheckStockShortagePromise(
    ipBoardPart: IPBoardPart,
    otherIPBoardPart: IPBoardPart
  ): Promise<boolean> {
    const neededStock =
      ipBoardPart == null
        ? 0
        : IPBoardHelper.getNeededStockForBoardPart(
            ipBoardPart,
            otherIPBoardPart == null,
            this.subProject.projectPlacement != null &&
              this.subProject.projectPlacement.secondBoard != null &&
              this.subProject.projectPlacement.secondBoard.secondBoardCopy
          );
    if (neededStock > 0) {
      return this.stockForSignControllerRequestService
        .totalStockAvailableForOrderForSign(ipBoardPart.sign.id)
        .then((availableStock: number) => availableStock < neededStock);
    } else {
      return Promise.resolve(false);
    }
  }

  closeTab() {
    this.tabsService.closeTab(
      this.tabsService.getActiveTab(),
      null,
      false,
      true
    );
  }

  cancel() {
    this.modalService.confirmDialog(
      'Annuleren',
      'Bent u zeker dat u het indienen van dit project wilt annuleren? Opgelet: De reeds ingevulde informatie zal verloren gaan.',
      (b) => {
        if (b) {
          this.tabsService.closeTab(
            this.tabsService.getActiveTab(),
            '/client-corner',
            false
          );
        }
      }
    );
  }

  submit() {
    if (this.checkCanSubmitProject()) {
      this.submitting = true;
      if (
        this.addressWasHidden &&
        this.subProject.projectPlacement != null &&
        this.subProject.projectPlacement.address != null
      ) {
        this.subProject.projectPlacement.address.line1 = null;
        this.subProject.projectPlacement.address.postalCode = null;
        this.subProject.projectPlacement.address.city = null;
        this.subProject.projectPlacement.address.province = null;
        this.subProject.projectPlacement.address.country = null;
        this.subProject.projectPlacement.address.coordinates = null;
      }
      this.submitProject.emit();
    }
  }

  checkCanSubmitProject() {
    if (
      this.project.projectContacts == null ||
      this.project.projectContacts.length === 0
    ) {
      this.snackService.showWarning(
        'Gelieve een contactpersoon in te vullen onder Bedrijf'
      );
      return false;
    }
    if (this.project.products == null || this.project.products.length === 0) {
      this.snackService.showWarning(
        'Gelieve minstens één product toe te voegen onder Project'
      );
      return false;
    }
    if (this.project.products.some((p) => p.type == null)) {
      this.snackService.showWarning(
        'Gelieve een type te selecteren voor elk product onder Project'
      );
      return false;
    }
    if (
      !CreateViewBaseProjectHelper.validateExtraCompanyContacts(this.project)
    ) {
      this.snackService.showWarning(
        'Gelieve een waarde in te vullen voor alle bedrijfsnamen en contactpersonen'
      );
      return false;
    }
    return true;
  }

  approveBaseProject(): void {
    const verificationError = ClientBaseProjectSummaryHelper.verifyApproval(
      this.project,
      this.subProject
    );
    if (verificationError != null) {
      this.snackService.showError(verificationError);
      return;
    }
    if (
      ProjectHelper.mustHaveAppointment(this.subProject.projectPlacement) &&
      !(
        this.subProject.projectPlacement.appointment ||
        this.subProject.projectPlacement.collectKey
      )
    ) {
      this.snackService.showError(
        'Er moet een afspraak gemaakt worden voor een plaatsing boven het 2e verdiep.'
      );
      return;
    }
    if (
      !ClientBaseProjectSummaryHelper.showWarningForApproval(
        this.project.products
      )
    ) {
      this.doToggleApproval();
    } else {
      this.modalService.confirmDialog(
        'Impressie ontbreekt',
        'Er is geen impressie aanwezig voor minstens één product. Weet u zeker dat u wilt goedkeuren?',
        (c) => {
          if (c) {
            this.doToggleApproval();
          }
        }
      );
    }
  }

  private doToggleApproval(): void {
    this.loadBeforeClose = true;

    this.toggleSubProjectApprovalControllerRequestService
      .toggleSubProjectApproval(this.subProject.id)
      .then((res: ToggleSubProjectApprovalResponse) => {
        this.subProject.approved = res.newApprovalValue;
        this.subProject.approvedRegistration = res.approvedRegistration;
        this.project.products.forEach(
          (p) => (p.approvalDate = res.productApprovalDate)
        );
        if (res.newApprovalValue) {
          this.tabsService.closeTab(
            this.tabsService.getActiveTab(),
            null,
            false
          );
        }
      })
      .finally(() => (this.loadBeforeClose = false));
  }

  unapproveBaseProject(): void {
    this.doToggleApproval();
  }

  setDesignFinished(): void {
    let error = null;

    if (!CollectionHelper.isPopulated(this.project.products)) {
      error = 'Project heeft geen producten';
    }

    if (error == null) {
      for (const product of this.project.products) {
        if (
          !CollectionHelper.isPopulated(product.productAssets) ||
          !product.productAssets.some(
            (pa) => !pa.archived && pa.formSection === 'extraDesign'
          )
        ) {
          error = 'Er is nog geen ontwerp toegevoegd';
          break;
        }
        if (!product.designSentToCustomer) {
          error = 'Ontwerp is nog niet verzonden naar klant';
          break;
        }
        if (!product.customerApproves) {
          error = 'Ontwerp is nog niet goedgekeurd door klant';
          break;
        }
      }
    }

    if (error != null) {
      this.snackService.showError(error);
    } else {
      this.project.products.forEach((p) => {
        p.designFinished = true;
      });
      this.designState = false;
      this.save.emit();
    }
  }

  openOnHoldDialog(): void {
    this.modalService.openComponentInModal(
      IPBoardOnHoldDialogComponent,
      [
        { key: 'project', value: this.project },
        { key: 'subProject', value: this.subProject },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.close,
        (b: boolean) => {
          if (b) {
            this.projectDisplayObservableService
              .getSubProjectSetOnHoldSubscribable()
              .next(this.subProject.id);
          }
        }
      ),
      { minWidth: 750 }
    );
  }

  openDesignDialog(): void {
    this.modalService.openComponentInModal(
      IPBoardDesignDialogComponent,
      [
        { key: 'project', value: this.project },
        { key: 'baseProject', value: this.subProject },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.close,
        (b: boolean) => {
          if (b) {
            this.designState = true;
            this.projectDisplayObservableService
              .getProductDesignVariableChangedSubscribable()
              .next();

            this.tabsService.closeTab(this.tabsService.getActiveTab(), '/immo');
          }
        }
      ),
      { minWidth: 750 }
    );
  }

  openPrintworkDialog(): void {
    this.modalService.openComponentInModal(
      IPBoardPrintworkDialogComponent,
      [
        { key: 'project', value: this.project },
        { key: 'baseProject', value: this.subProject },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.close,
        (b: boolean) => {
          if (b) {
            this.projectDisplayObservableService
              .getProductPrintworkVariableChangedSubscribable()
              .next();
          }
        }
      ),
      { minWidth: 750 }
    );
  }

  protected readonly STATUS = STATUS;
}
